import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
// DO NOT import initializeAllTrackersBasedOnConsent here for re-exporting.
// It will be imported and used by tracking.ts itself, or App.tsx directly from tracking.ts.

export type TrackingCategory = 'analytics' | 'advertising' | 'functional' | 'performance';
export type ConsentStatus = 'granted' | 'denied' | 'pending';

export type Jurisdiction = 'EU' | 'CA' | 'US_OTHER' | 'OTHER' | 'UNKNOWN';
export interface UserLocationData {
  ipDetected?: string;
  jurisdiction: Jurisdiction;
  countryCode?: string;
  regionCode?: string;
}

export interface ConsentState {
  consent: Record<TrackingCategory, ConsentStatus>;
  jurisdiction: Jurisdiction;
  hasMadeChoice: boolean; 
  isConsentBannerOpen: boolean;
  isPreferencesModalOpen: boolean;
  
  setConsent: (category: TrackingCategory, status: ConsentStatus) => void;
  setAllConsents: (status: 'granted' | 'denied') => void;
  optOutOfSaleAndSharing: () => void; 
  setJurisdiction: (jurisdiction: ConsentState['jurisdiction']) => void;
  setHasMadeChoice: (choice: boolean) => void;
  openConsentBanner: () => void;
  closeConsentBanner: () => void;
  openPreferencesModal: () => void;
  closePreferencesModal: () => void;
  initializeConsents: (detectedJurisdiction: ConsentState['jurisdiction']) => void;
  resetConsentToDefaultsForJurisdiction: (jurisdiction: ConsentState['jurisdiction']) => void;
}

const getDefaultConsents = (jurisdiction: ConsentState['jurisdiction']): Record<TrackingCategory, ConsentStatus> => {
  const defaults: Record<TrackingCategory, ConsentStatus> = {
    functional: 'granted', 
    performance: 'granted',
    analytics: 'granted',
    advertising: 'granted',
  };
  if (jurisdiction === 'EU') {
    defaults.performance = 'pending';
    defaults.analytics = 'pending';
    defaults.advertising = 'pending';
  } else if (jurisdiction === 'CA') {
    defaults.performance = 'pending';
    defaults.analytics = 'pending';   
    defaults.advertising = 'pending'; 
  }
  return defaults;
};

export const useConsentStore = create<ConsentState>()(
  persist(
    (set, get) => ({
      consent: getDefaultConsents('UNKNOWN'),
      jurisdiction: 'UNKNOWN',
      hasMadeChoice: false,
      isConsentBannerOpen: false,
      isPreferencesModalOpen: false,

      setConsent: (category, status) => {
        if (category === 'functional' && status === 'denied') return; 
        set((state) => ({
          consent: { ...state.consent, [category]: status },
          hasMadeChoice: true, 
          isConsentBannerOpen: false, 
        }));
      },
      setAllConsents: (status) => {
        const newConsents = { ...get().consent }; 
        newConsents.functional = 'granted'; 
        (Object.keys(newConsents) as TrackingCategory[]).forEach(key => {
          if (key !== 'functional') {
            newConsents[key] = status;
          }
        });
        set({
          consent: newConsents,
          hasMadeChoice: true,
          isConsentBannerOpen: false,
        });
      },
      optOutOfSaleAndSharing: () => {
        set((state) => ({
          consent: {
            ...state.consent,
            advertising: 'denied',
            analytics: 'denied', 
          },
          hasMadeChoice: true,
          isConsentBannerOpen: false,
        }));
        console.log("User opted out of sale/sharing: advertising and analytics denied.");
      },
      setJurisdiction: (jurisdiction) => set({ jurisdiction }),
      setHasMadeChoice: (choice) => set({ hasMadeChoice: choice, isConsentBannerOpen: !choice }),
      openConsentBanner: () => set({ isConsentBannerOpen: true }), 
      closeConsentBanner: () => set({ isConsentBannerOpen: false, hasMadeChoice: true }),
      openPreferencesModal: () => set({ isPreferencesModalOpen: true, isConsentBannerOpen: false }),
      closePreferencesModal: () => set({ isPreferencesModalOpen: false }),
      
      initializeConsents: (detectedJurisdiction) => {
        const currentStoredState = get();
        if (currentStoredState.hasMadeChoice && currentStoredState.jurisdiction === detectedJurisdiction) {
          set({ isConsentBannerOpen: false }); 
          // The tracking.ts subscription will handle calling initializeAllTrackersBasedOnConsent
          // We could call it here explicitly too if needed, but subscription should be enough.
          // import('@/lib/tracking').then(tracking => tracking.initializeAllTrackersBasedOnConsent());
          return;
        }
        console.log(`Initializing consents for jurisdiction: ${detectedJurisdiction}. Has made choice previously: ${currentStoredState.hasMadeChoice}`);
        set({
          jurisdiction: detectedJurisdiction,
          consent: getDefaultConsents(detectedJurisdiction),
          hasMadeChoice: false, 
          isConsentBannerOpen: (detectedJurisdiction === 'EU' || detectedJurisdiction === 'CA'),
        });
      },
      resetConsentToDefaultsForJurisdiction: (jurisdictionToResetTo) => {
        set({
            consent: getDefaultConsents(jurisdictionToResetTo),
            jurisdiction: jurisdictionToResetTo,
            hasMadeChoice: false, 
            isConsentBannerOpen: (jurisdictionToResetTo === 'EU' || jurisdictionToResetTo === 'CA'),
        });
      }
    }),
    {
      name: 'syncabill-consent-storage', // SHARED KEY
      storage: createJSONStorage(() => localStorage), 
      onRehydrateStorage: () => (state) => { 
        if (state) {
          console.log("Frontend consentStore rehydrated. HasMadeChoice:", state.hasMadeChoice, "Consents:", state.consent);
          // Defer tracker initialization to App.tsx or tracking.ts subscription
        }
      }
    }
  )
);
